import { Link } from "react-router-dom";
import { useSeo } from "../lib/seo";
import {
  Activity,
  ArrowRight,
  Boxes,
  CheckCircle2,
  Clock,
  Database,
  HeartPulse,
  Lock,
  MessageSquare,
  Plug,
  Search,
  ShieldCheck,
  Sparkles,
  Store,
  Workflow,
  Zap,
} from "lucide-react";
import {
  siDatabricks,
  siHubspot,
  siJira,
  siMongodb,
  siMysql,
  siPostgresql,
  siSap,
  siShopify,
  siSnowflake,
  siStripe,
  siZendesk,
} from "simple-icons";
import { buttonVariants } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { Reveal } from "../components/Reveal";
import { IntegrationFlow } from "../components/IntegrationFlow";
import { HeroCanvas } from "../components/HeroCanvas";
import { DeployAnywhere } from "../components/DeployAnywhere";
import { ExchangeShowcase } from "../components/ExchangeShowcase";

type BrandItem = { name: string; hex: string; path?: string };
const BRANDS: BrandItem[] = [
  { name: "Salesforce", hex: "#00A1E0" },
  { name: "SAP", hex: `#${siSap.hex}`, path: siSap.path },
  { name: "Oracle", hex: "#C74634" },
  { name: "NetSuite", hex: "#1F5788" },
  { name: "ServiceNow", hex: "#62D84E" },
  { name: "Slack", hex: "#611f69" },
  { name: "Jira", hex: `#${siJira.hex}`, path: siJira.path },
  { name: "Snowflake", hex: `#${siSnowflake.hex}`, path: siSnowflake.path },
  { name: "PostgreSQL", hex: "#4a90d9", path: siPostgresql.path },
  { name: "MySQL", hex: "#8fb9d9", path: siMysql.path },
  { name: "Workday", hex: "#F68D2E" },
  { name: "HubSpot", hex: `#${siHubspot.hex}`, path: siHubspot.path },
  { name: "Zendesk", hex: "#a0d3c8", path: siZendesk.path },
  { name: "Stripe", hex: "#a78bfa", path: siStripe.path },
  { name: "Shopify", hex: `#${siShopify.hex}`, path: siShopify.path },
  { name: "MongoDB", hex: `#${siMongodb.hex}`, path: siMongodb.path },
  { name: "Databricks", hex: "#FF5c47", path: siDatabricks.path },
];

function DarkChip({ b }: { b: BrandItem }) {
  return (
    <span className="flex shrink-0 items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.04] px-5 py-2.5 backdrop-blur">
      {b.path ? (
        <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" aria-hidden="true">
          <path d={b.path} fill={b.hex} />
        </svg>
      ) : (
        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: b.hex }} />
      )}
      <span className="whitespace-nowrap text-sm font-semibold tracking-tight text-slate-200">{b.name}</span>
    </span>
  );
}

// Deterministic star field for the hero (x%, y%, r px, twinkle delay/dur).
const STARS = [
  [6, 18, 1, 0, 4], [14, 62, 1.4, 1.2, 5], [22, 30, 1, 0.6, 3.5], [31, 80, 1.2, 2.1, 4.5],
  [40, 12, 1, 0.3, 5], [9, 88, 1.3, 1.7, 4], [48, 68, 1, 2.4, 3.8], [58, 22, 1.5, 0.9, 5],
  [66, 84, 1, 1.4, 4.2], [73, 40, 1.2, 2.6, 3.6], [82, 16, 1, 0.5, 4.8], [90, 60, 1.4, 1.9, 5],
  [95, 34, 1, 1.1, 4], [3, 44, 1, 2.2, 4.6], [37, 52, 1, 0.8, 3.7], [52, 90, 1.3, 1.5, 5],
  [78, 70, 1, 2.0, 4.3], [88, 90, 1.2, 0.4, 4], [64, 8, 1, 1.6, 5], [18, 8, 1.1, 2.3, 4.4],
] as const;

// Sparse particle field for the dark security section.
const SEC_STARS = [
  [8, 24, 1, 0.4, 4], [20, 70, 1.2, 1.5, 5], [34, 40, 1, 2.2, 3.6], [46, 82, 1, 0.9, 4.4],
  [58, 30, 1.3, 1.8, 5], [70, 66, 1, 0.5, 4], [82, 22, 1.1, 2.4, 4.6], [92, 58, 1, 1.2, 3.8],
  [15, 50, 1, 2.0, 5], [63, 88, 1, 1.0, 4.2], [88, 84, 1.2, 0.7, 4], [40, 14, 1, 1.6, 5],
] as const;

const FLOW_STEPS = [
  { n: "01", title: "Describe it", body: "“Connect our on-prem SAP over OAuth2 and let me query open orders.” That's the whole spec." },
  { n: "02", title: "AI builds it", body: "Wise 2.0 writes the connector, its parameter schema, and a full test suite — in minutes." },
  { n: "03", title: "Sandbox proves it", body: "Everything runs isolated in a gVisor sandbox with no network. Tests must pass to ship." },
  { n: "04", title: "Live & monitored", body: "The signed config registers the connection, and the monitoring agent keeps it healthy." },
];

const STATS = [
  { big: "< 5 min", small: "from a plain-English prompt to a sandbox-tested connector", gradient: true },
  { big: "100%", small: "of AI-generated code runs isolated in a gVisor sandbox" },
  { big: "24/7", small: "monitoring agent keeps every connection healthy" },
  { big: "0", small: "credentials ever leave Vault — configs carry references only" },
];

const LAYERS = [
  { icon: Plug, tag: "Connectivity", title: "Connect anything by describing it", body: "Describe a system in chat — AI writes the connector, tests it in a secure gVisor sandbox, signs the config as portable XML, and registers it as a tool." },
  { icon: Workflow, tag: "Automation", title: "Automate across systems", body: "Turn natural language into scheduled, portable workflows. A visual mapper reshapes fields with golden-input tests. Jobs are XML — air-gap friendly." },
  { icon: Search, tag: "Experience", title: "Ask across your whole stack", body: "The agent decomposes a question into parallel calls across connectors, reconciles the results, and renders a comparison with citations back to each source." },
];

const HEALTH_FEED = [
  { tone: "ok", system: "salesforce", event: "health check passed · 142 ms", when: "2 min ago" },
  { tone: "ok", system: "netsuite", event: "access token refreshed automatically", when: "1 h ago" },
  { tone: "warn", system: "sap", event: "certificate expires in 14 days → renewal scheduled", when: "today" },
  { tone: "ok", system: "postgres-dwh", event: "schema drift detected → mapping updated & re-tested", when: "yesterday" },
] as const;

const TRUST = [
  { icon: ShieldCheck, t: "Sandboxed by default", d: "AI-generated code runs only in gVisor with per-connection egress control." },
  { icon: Lock, t: "Secrets in Vault", d: "Credentials live in a vault; signed configs carry references, never values." },
  { icon: Database, t: "XML-portable configs", d: "Ed25519-signed configs in object storage — copy between servers, air-gap ready." },
  { icon: Store, t: "Connector Exchange", d: "Publish once, install in any workspace — schema travels, secrets never do." },
];

export default function Landing() {
  useSeo({
    title: 'Wise 2.0 by Shineywise — AI-Powered Enterprise Integration Platform',
    description: 'Connect Salesforce, SAP, databases and any REST API in plain English. Generate tested connectors from a prompt, automate workflows, publish secure APIs, and query across every system with AI — no code required.',
    path: '/',
  });
  return (
    <div className="overflow-hidden">
      {/* 1 · Hero — cinematic dark, living canvas */}
      <section className="relative overflow-hidden bg-slate-950">
        <div className="pointer-events-none absolute inset-0 bg-aurora opacity-70" />
        <div className="pointer-events-none absolute inset-0 bg-grid [background-size:44px_44px] [mask-image:radial-gradient(80%_60%_at_50%_0%,black,transparent)] opacity-[0.15]" />
        <div className="pointer-events-none absolute -left-32 top-0 h-96 w-96 rounded-full bg-accent-600/25 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 top-32 h-96 w-96 rounded-full bg-brand-500/20 blur-3xl" />
        {/* star field */}
        <div className="pointer-events-none absolute inset-0">
          {STARS.map(([x, y, r, delay, dur], i) => (
            <span
              key={i}
              className="twinkle absolute rounded-full bg-white"
              style={{ left: `${x}%`, top: `${y}%`, width: `${r * 2}px`, height: `${r * 2}px`, animationDelay: `${delay}s`, animationDuration: `${dur}s` }}
            />
          ))}
        </div>

        <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-6 pb-16 pt-24 lg:grid-cols-2 lg:pb-24 lg:pt-32">
          <div className="text-center lg:text-left">
            <div className="animate-fade-in">
              <Badge tone="violet" className="mx-auto border border-white/10 bg-white/10 text-accent-200 lg:mx-0">
                <Sparkles className="h-3.5 w-3.5" /> AI-native integration platform
              </Badge>
            </div>
            <h1 className="mt-6 animate-slide-up text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-6xl">
              Integrate every system,
              <br />
              <span className="bg-gradient-to-r from-sky-300 via-accent-300 to-accent-400 bg-clip-text text-transparent">with a sentence.</span>
            </h1>
            <p className="mx-auto mt-6 max-w-xl animate-slide-up text-lg leading-relaxed text-slate-300 [animation-delay:80ms] lg:mx-0">
              Describe a system in plain English — Wise 2.0 builds the connector, proves it in an
              isolated sandbox, signs it, and keeps it healthy. No glue code. No six-week projects.
            </p>
            <div className="mt-9 flex animate-slide-up flex-col items-center justify-center gap-3 [animation-delay:160ms] sm:flex-row lg:justify-start">
              <Link to="/signup" className={buttonVariants({ size: "lg", className: "group" })}>
                Start free <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              <a href="#how" className="inline-flex h-12 items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-6 text-base font-semibold text-white backdrop-blur transition hover:bg-white/10">
                See how it works
              </a>
            </div>
            <p className="mt-5 animate-fade-in text-sm text-slate-400 [animation-delay:260ms]">
              Free pilot workspace · no credit card · deploy on-prem or in your cloud
            </p>
          </div>

          <div className="animate-scale-in [animation-delay:220ms]">
            <div className="relative rounded-3xl border border-white/10 bg-white/[0.03] p-5 shadow-lift backdrop-blur-xl sm:p-7">
              <div className="mb-3 flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-rose-400/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
                <span className="ml-2 text-xs font-medium text-slate-400">live integration graph</span>
              </div>
              <HeroCanvas />
            </div>
          </div>
        </div>

        {/* systems marquee (dark) */}
        <div className="relative border-t border-white/10 py-8">
          <p className="text-center text-xs font-semibold uppercase tracking-wider text-slate-500">
            Connects to the systems you already run — and any API you can describe
          </p>
          <div className="mt-5 overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]">
            <div className="flex w-max animate-marquee gap-4 pr-4">
              {[...BRANDS, ...BRANDS].map((b, i) => (
                <DarkChip key={`${b.name}-${i}`} b={b} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 2 · How it works — animated integration flow */}
      <section id="how" className="mx-auto max-w-6xl scroll-mt-20 px-6 py-24">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <Badge tone="violet" className="mx-auto">
              <Sparkles className="h-3.5 w-3.5" /> How it works
            </Badge>
            <h2 className="mt-5 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              From a sentence to a live integration
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Watch the whole journey: your systems flow through the AI hub, get proven in an
              isolated sandbox, and come out as signed, monitored connections.
            </p>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <div className="mt-12 rounded-3xl border border-slate-200 bg-white/80 p-6 shadow-card sm:p-10">
            <IntegrationFlow />
          </div>
        </Reveal>

        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {FLOW_STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 90}>
              <div className="h-full rounded-2xl border border-slate-200 bg-slate-50/60 p-6">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-gradient text-sm font-bold text-white shadow-lift">{s.n}</span>
                <h3 className="mt-4 text-base font-semibold text-slate-900">{s.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 3 · Deploy anywhere (dark) */}
      <DeployAnywhere />

      {/* 4 · Minutes, not months */}
      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-6 py-24">
          <Reveal>
            <div className="mx-auto max-w-2xl text-center">
              <Badge tone="brand" className="mx-auto"><Clock className="h-3.5 w-3.5" /> Minutes, not months</Badge>
              <h2 className="mt-5 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                A new integration is minutes away
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                The old way needs discovery calls, spec documents, glue code and a QA cycle.
                Here, you describe the outcome — the AI does the wiring, the sandbox proves it.
              </p>
            </div>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            <Reveal>
              <div className="h-full rounded-2xl border border-slate-200 bg-slate-50/70 p-7">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Traditional integration project</p>
                <p className="mt-2 text-2xl font-bold text-slate-500">6–12 weeks</p>
                <div className="mt-5 space-y-2.5">
                  {["Discovery & vendor docs", "Spec + security review", "Glue code & auth plumbing", "QA cycle and re-work", "Deploy window"].map((t, i) => (
                    <div key={t} className="flex items-center gap-3">
                      <div className="bar-grow h-2 rounded-full bg-slate-300" style={{ width: `${88 - i * 12}%`, transitionDelay: `${i * 90}ms` }} />
                      <span className="whitespace-nowrap text-xs text-slate-400">{t}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <div className="h-full rounded-2xl border border-brand-200 bg-white p-7 shadow-lift ring-1 ring-inset ring-brand-100">
                <p className="text-xs font-semibold uppercase tracking-wider text-brand-600">With Wise 2.0</p>
                <p className="mt-2 text-2xl font-bold text-gradient">Minutes</p>
                <div className="mt-5 space-y-2.5">
                  {[
                    { t: "Describe the system in chat", w: "34%" },
                    { t: "AI builds connector + tests in the sandbox", w: "52%" },
                    { t: "Signed, registered, live", w: "22%" },
                  ].map((s, i) => (
                    <div key={s.t} className="flex items-center gap-3">
                      <div className="bar-grow h-2 rounded-full bg-brand-gradient" style={{ width: s.w, transitionDelay: `${200 + i * 60}ms` }} />
                      <span className="whitespace-nowrap text-xs text-slate-500">{s.t}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-5 flex items-center gap-2 text-sm font-medium text-slate-600">
                  <Zap className="h-4 w-4 text-amber-500" /> Then reuse it everywhere via the Connector Exchange.
                </p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={100}>
            <div className="mt-10 grid gap-8 rounded-3xl border border-slate-200 bg-slate-50/60 px-8 py-8 sm:grid-cols-2 lg:grid-cols-4">
              {STATS.map((s) => (
                <div key={s.big} className="text-center sm:text-left">
                  <p className={`text-3xl font-extrabold tracking-tight ${s.gradient ? "text-gradient" : "text-slate-900"}`}>{s.big}</p>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{s.small}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* 5 · Platform breadth */}
      <section id="features" className="mx-auto max-w-6xl px-6 py-24">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <Badge tone="brand" className="mx-auto"><Boxes className="h-3.5 w-3.5" /> Platform</Badge>
            <h2 className="mt-5 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">One platform, three layers</h2>
            <p className="mt-4 text-lg text-slate-600">
              Connectivity, automation, and a natural-language experience — all driven by an
              AI agent that can read, write, and reason over your entire stack.
            </p>
          </div>
        </Reveal>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {LAYERS.map(({ icon: Icon, tag, title, body }, i) => (
            <Reveal key={tag} delay={i * 90}>
              <div className="group h-full rounded-2xl border border-slate-200 bg-white p-7 shadow-card transition-all hover:-translate-y-1 hover:shadow-lift">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-600 ring-1 ring-inset ring-brand-100 transition-all duration-300 group-hover:bg-brand-gradient group-hover:text-white group-hover:shadow-glow">
                  <Icon className="h-6 w-6 transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-3" />
                </div>
                <p className="mt-5 text-xs font-semibold uppercase tracking-wider text-accent-600">{tag}</p>
                <h3 className="mt-1.5 text-lg font-semibold text-slate-900">{title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-slate-500">{body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 6 · Wise Exchange — the live catalog */}
      <ExchangeShowcase />

      {/* 7 · Federated query */}
      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-6 py-24">
          <div className="grid items-center gap-12 md:grid-cols-2">
            <Reveal>
              <div>
                <Badge tone="brand"><MessageSquare className="h-3.5 w-3.5" /> Federated query</Badge>
                <h2 className="mt-5 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Ask one question. Query every system.</h2>
                <p className="mt-4 text-slate-600">
                  The agent fans a natural-language question out across connectors in parallel,
                  reconciles results with deterministic and fuzzy matching, and shows a comparison
                  you can trust — every value cited back to its source.
                </p>
                <Link to="/signup" className={buttonVariants({ className: "group mt-7" })}>
                  Start free <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
                <div className="flex items-center gap-2 rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-500">
                  <Search className="h-4 w-4" /> Which accounts exist in Salesforce but not in SAP?
                </div>
                <div className="mt-4 space-y-2">
                  {[
                    { name: "Acme Corp", b: false },
                    { name: "Globex", b: true },
                    { name: "Initech", b: false },
                  ].map((r) => (
                    <div key={r.name} className="flex items-center justify-between rounded-lg border border-slate-100 px-4 py-2.5 text-sm">
                      <span className="font-medium text-slate-700">{r.name}</span>
                      <span className="flex items-center gap-4 text-xs">
                        <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-brand-500" />SF</span>
                        <span className={r.b ? "text-slate-500" : "font-semibold text-rose-600"}>{r.b ? "SAP ✓" : "SAP ✗"}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 7 · Day 2 — monitoring agent */}
      <section className="mx-auto max-w-6xl px-6 py-24">
        <div className="grid items-center gap-12 md:grid-cols-2">
          <Reveal>
            <div>
              <Badge tone="green"><HeartPulse className="h-3.5 w-3.5" /> Monitoring agent</Badge>
              <h2 className="mt-5 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Maintenance is now super easy.</h2>
              <p className="mt-4 leading-relaxed text-slate-600">
                The monitoring agent re-tests every connection around the clock, refreshes
                credentials before they expire, and heals configuration drift automatically.
                You get a friendly notification — not a 2 a.m. incident call.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  "Continuous health checks with real latency budgets",
                  "Token & certificate renewals handled before they bite",
                  "Schema-drift detection with auto-updated mappings",
                  "Every fix re-verified in the sandbox before it ships",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-sm text-slate-600">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-card">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <Activity className="h-4 w-4 text-emerald-500" /> Connection health
                </span>
                <Badge tone="green">All systems go</Badge>
              </div>
              <ul className="mt-3 space-y-2.5">
                {HEALTH_FEED.map((h, i) => (
                  <li key={h.event} className="flex items-start justify-between gap-3 rounded-xl border border-slate-100 px-4 py-3">
                    <span className="flex items-start gap-2.5">
                      <span
                        className={`dot-beat mt-1.5 h-2 w-2 shrink-0 rounded-full ${h.tone === "ok" ? "bg-emerald-500" : "bg-amber-400"}`}
                        style={{ animationDelay: `${i * 0.6}s`, animationDuration: h.tone === "ok" ? "2.4s" : "3.2s" }}
                      />
                      <span className="text-sm">
                        <code className="font-semibold text-slate-800">{h.system}</code>
                        <span className="text-slate-500"> — {h.event}</span>
                      </span>
                    </span>
                    <span className="shrink-0 text-xs text-slate-400">{h.when}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 8 · Security — dark, animated */}
      <section className="relative overflow-hidden bg-slate-950 py-24 text-slate-200">
        <div className="pointer-events-none absolute inset-0 bg-aurora opacity-40" />
        <div className="pointer-events-none absolute -left-24 top-8 h-72 w-72 animate-blob rounded-full bg-accent-600/20 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 bottom-0 h-72 w-72 animate-blob rounded-full bg-brand-500/20 blur-3xl [animation-delay:4s]" />
        {/* drifting particle field */}
        <div className="pointer-events-none absolute inset-0">
          {SEC_STARS.map(([x, y, r, delay, dur], i) => (
            <span
              key={i}
              className="twinkle absolute rounded-full bg-white"
              style={{ left: `${x}%`, top: `${y}%`, width: `${r * 2}px`, height: `${r * 2}px`, animationDelay: `${delay}s`, animationDuration: `${dur}s` }}
            />
          ))}
        </div>

        <div className="relative mx-auto max-w-6xl px-6">
          <Reveal>
            <div className="mx-auto max-w-2xl text-center">
              <Badge tone="green" className="mx-auto border border-white/10 bg-white/10 text-emerald-300">
                <ShieldCheck className="h-3.5 w-3.5" /> Security &amp; trust
              </Badge>
              <h2 className="mt-5 text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Secure by architecture, not by policy
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-slate-400">
                Isolation, signing, and secret management are built into how the platform runs —
                not bolted on afterwards.
              </p>
            </div>
          </Reveal>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {TRUST.map(({ icon: Icon, t, d }, i) => (
              <Reveal key={t} delay={i * 90}>
                <div className="group relative h-full overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-sm transition-colors hover:border-white/20">
                  {/* sweeping sheen on hover */}
                  <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/[0.06] to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                  <span className="relative flex h-12 w-12 items-center justify-center rounded-xl bg-brand-gradient text-white shadow-glow">
                    <span aria-hidden className="glow-breathe absolute inset-0 rounded-xl bg-accent-500/40 blur-md" style={{ animationDelay: `${i * 0.6}s` }} />
                    <Icon className="relative h-6 w-6" />
                  </span>
                  <p className="relative mt-5 text-base font-semibold text-white">{t}</p>
                  <p className="relative mt-1.5 text-sm leading-relaxed text-slate-400">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 9 · Close — light */}
      <section className="relative overflow-hidden bg-white py-24">
        <div className="pointer-events-none absolute inset-0 bg-aurora opacity-60" />
        <div className="pointer-events-none absolute inset-0 bg-grid [background-size:44px_44px] [mask-image:radial-gradient(60%_60%_at_50%_50%,black,transparent)] opacity-40" />
        <div className="relative mx-auto max-w-3xl px-6 text-center">
          <Reveal>
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-gradient text-white shadow-glow">
              <Zap className="h-7 w-7" />
            </span>
            <h2 className="mt-6 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Ship your first integration today
            </h2>
            <p className="mx-auto mt-4 max-w-lg text-lg text-slate-600">
              Describe a system in plain English. The AI does the wiring, the sandbox proves it,
              the monitoring agent keeps it healthy — on your infrastructure or ours.
            </p>
            <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link to="/signup" className={buttonVariants({ size: "lg", className: "group" })}>
                Create your workspace <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              <a href="#how" className={buttonVariants({ variant: "outline", size: "lg" })}>
                See how it works
              </a>
            </div>
            <p className="mt-5 text-sm text-slate-400">Free pilot workspace · no credit card required</p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
